import { Upload, X, FileText, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useUploadWaiverTemplate } from '../../hooks/useWaivers';

export default function UploadWaiverPage() {
  const navigate = useNavigate();
  const { data: currentCompanyId, isLoading: isLoadingCompany } = useCurrentCompany();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [waiverCategory, setWaiverCategory] = useState<'adult' | 'child' | 'family' | 'general'>('general');
  const requiresChild = waiverCategory === 'child' || waiverCategory === 'family';
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadWaiverTemplate();

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Accept PDF and image files
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const validExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];

    const isValidType = validTypes.includes(file.type) || 
                       validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidType) {
      showNotification('error', 'Please select a valid PDF or image file (PDF, PNG, JPG, JPEG).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      showNotification('error', 'File size must be less than 10MB.');
      return;
    }

    // Ensure file size is available
    if (file.size === 0) {
      console.warn('File size is 0, this might indicate a problem reading the file');
    }

    setSelectedFile(file);
    // Auto-populate name if empty
    if (!name) {
      const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      setName(fileName);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showNotification('error', 'Please select a file to upload.');
      return;
    }

    if (!name.trim()) {
      showNotification('error', 'Please provide a template name.');
      return;
    }

    if (!currentCompanyId) {
      showNotification('error', 'Unable to determine company. Please refresh the page and try again.');
      return;
    }

    try {
      const fileData = await selectedFile.arrayBuffer();
      const contentType = selectedFile.type || 'application/pdf';

      const result = await uploadMutation.upload({
        fileData,
        companyId: currentCompanyId,
        name: name.trim(),
        description: description.trim() || undefined,
        contentType,
        requiresChild,
        waiverCategory,
      });

      showNotification('success', 'Waiver template uploaded successfully!');
      // Navigate to fields page to configure the template
      setTimeout(() => navigate(`/damage-waivers/${result.id}/fields`), 1500);
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string'
        ? error
        : 'Unknown error occurred. Please try again.';
      showNotification('error', `Failed to upload waiver: ${errorMessage}`);
    }
  };

  const handleCancel = () => {
    navigate('/damage-waivers');
  };

  if (isLoadingCompany && !selectedFile) {
    return (
      <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
          <p className="mt-2 text-[#A9A3A3]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
        {/* Notification Toast */}
        {notification && (
          <div
            className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-top-5 ${
              notification.type === 'success'
                ? 'bg-green-600 text-white'
                : 'bg-red-600 text-white'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-70 transition-opacity"
            >
              ×
            </button>
          </div>
        )}

        {/* Page Header - matches Billing Settings styling */}
        <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">Upload Waiver Template</h1>
              <p className="text-white/80 mt-1">Upload a PDF or image file for your waiver template</p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-2 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Waivers
            </button>
          </div>
        </div>

        {/* Content - fills remaining area (layout provides horizontal padding) */}
        <div className="flex-1 bg-[#111010] overflow-auto scrollbar-styled min-h-0 py-6 w-full">

        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE]">Waiver Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragOver
                  ? 'border-[#9B1421] bg-[#9B1421]/10'
                  : 'border-[#504A4A] hover:border-[#9B1421]'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                onChange={handleFileInputChange}
                className="hidden"
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-4">
                  <FileText className="w-12 h-12 text-text-primary" />
                  <div className="text-left">
                    <p className="text-[#EBFFEE] font-medium">{selectedFile.name}</p>
                    <p className="text-[#A9A3A3] text-sm">
                      {selectedFile.size && selectedFile.size > 0
                        ? selectedFile.size < 1024
                          ? `${selectedFile.size} bytes`
                          : selectedFile.size < 1024 * 1024
                          ? `${(selectedFile.size / 1024).toFixed(2)} KB`
                          : `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                        : 'File size unavailable'}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-[#A9A3A3] hover:text-red-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-[#A9A3A3] mx-auto mb-4" />
                  <p className="text-[#EBFFEE] mb-2">
                    Drag and drop your waiver file here, or click to browse
                  </p>
                  <p className="text-[#A9A3A3] text-sm">
                    Supports PDF, PNG, JPG, JPEG (max 10MB)
                  </p>
                </>
              )}
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Template Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Standard Liability Waiver"
                  maxLength={100}
                  className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this waiver template"
                  rows={3}
                  className="w-full px-4 py-2 bg-[#363232] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421] resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Waiver Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      { value: 'general', label: 'General', desc: 'Applies to all members' },
                      { value: 'adult', label: 'Adult', desc: 'For members 18 and older' },
                      { value: 'child', label: 'Minor', desc: 'For members under 18' },
                      { value: 'family', label: 'Family', desc: 'Covers entire family unit' },
                    ] as const
                  ).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setWaiverCategory(opt.value)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        waiverCategory === opt.value
                          ? 'border-[#9B1421] bg-[#9B1421]/20 text-[#EBFFEE]'
                          : 'border-[#504A4A] bg-[#363232] text-[#A9A3A3] hover:border-[#9B1421]/50'
                      }`}
                    >
                      <p className="font-medium text-sm">{opt.label}</p>
                      <p className="text-xs mt-0.5 opacity-75">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              {!currentCompanyId && !isLoadingCompany && (
                <div className="w-full mb-2 p-3 bg-yellow-900/20 border border-yellow-600 rounded-lg">
                  <p className="text-yellow-300 text-sm">
                    ⚠️ Unable to determine your company. Please ensure you are logged in and have a company relationship.
                  </p>
                </div>
              )}
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || !name.trim() || !currentCompanyId || uploadMutation.isLoading || isLoadingCompany}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-6 py-2 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  !selectedFile 
                    ? 'Please select a file'
                    : !name.trim()
                    ? 'Please provide a template name'
                    : !currentCompanyId
                    ? 'Unable to determine company. Please refresh the page.'
                    : isLoadingCompany
                    ? 'Loading company information...'
                    : uploadMutation.isLoading
                    ? 'Uploading...'
                    : 'Upload waiver'
                }
              >
                {uploadMutation.isLoading ? 'Uploading...' : 'Upload Waiver'}
              </Button>
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-[#504A4A] text-[#A9A3A3] hover:bg-[#363232] px-6 py-2"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
  );
}

